/**
 * The Explorer's view of the graph.
 *
 * @remarks
 * The Explorer client reads the derived graph index like every other consumer
 * and projects it into the node-and-link payload its views were written
 * against. The projection is one pure function, so the client runs it on the
 * bundle the static builder wrote. It keeps the shape the views expect, quirks
 * included, until the views read the graph directly.
 */
import type { GraphEdge, GraphFile, LiveDocGraph } from "@live-documentation/engine/live-docs/graph";

import type {
    ExplorerDependencyReference,
    ExplorerGraphPayload,
    ExplorerLinkPayload,
    ExplorerNodePayload,
    ExplorerPublicSymbol,
    ExplorerTypeReference
} from "./types";

const ROLE_OF_KIND = {
    returns: "return",
    parameter: "parameter",
    extends: "extends",
    implements: "implements",
    constraint: "constraint"
} as const;

const ROLE_OF_LINE = {
    Returns: "return",
    Extends: "extends",
    Implements: "implements",
    Constraints: "constraint"
} as const;

const isReference = (edge: GraphEdge): boolean => edge.kind !== "import" && edge.kind !== "re-export";

/**
 * Projects the graph into the Explorer's node-and-link payload.
 */
export function explorerGraphOf(graph: LiveDocGraph): ExplorerGraphPayload {
    const links: ExplorerLinkPayload[] = [];
    const seen = new Map<string, ExplorerLinkPayload>();
    let dependenciesOutsideScan = 0;
    // Resolve the docs' anchors once, before presentation. A heading's kind/overload suffix is identity,
    // not decoration: dropping it merges, for example, Walk (interface) and walk (function).
    const names = new Map(Object.values(graph.files).map(file => [file.codePath,
        new Map(file.symbols.map(symbol => [symbol.slug, symbol.name]))]));
    const nameOfAnchor = (file: string, anchor: string | undefined): string | undefined => names.get(file)?.get(anchor) ?? anchor;

    const addLink = (
        source: string,
        target: string,
        kind: ExplorerLinkPayload["kind"],
        sourceSymbol?: string,
        targetSymbol?: string
    ): void => {
        if (source === target) {
            return;
        }
        const key = JSON.stringify([source, target, kind, sourceSymbol ?? "", targetSymbol ?? ""]);
        const existing = seen.get(key);
        if (existing) {
            existing.referenceCount = (existing.referenceCount ?? 1) + 1;
            return;
        }
        const link = { source, target, kind, sourceSymbol, targetSymbol, referenceCount: 1 };
        seen.set(key, link);
        links.push(link);
    };

    const nodes: ExplorerNodePayload[] = Object.values(graph.files).map(file => {
        const nameOfSlug = names.get(file.codePath)!;
        const dependencies: ExplorerDependencyReference[] = [];

        for (const edge of file.edges) {
            if (!isReference(edge)) {
                dependencies.push({
                    targetId: edge.to,
                    targetDocPath: edge.to ? graph.files[edge.to].docPath : undefined,
                    targetSymbol: edge.toSymbol,
                    label: edge.label,
                    raw: edge.link ?? edge.label,
                    resolved: edge.to !== undefined,
                    kind: "dependency"
                });
            } else if (edge.to && edge.to !== file.codePath) {
                const role = ROLE_OF_KIND[edge.kind as keyof typeof ROLE_OF_KIND];
                const sourceSymbol = nameOfSlug.get(edge.from) ?? edge.from ?? "";
                dependencies.push({
                    targetId: edge.to,
                    targetDocPath: graph.files[edge.to].docPath,
                    targetSymbol: edge.toSymbol,
                    sourceSymbol,
                    label: `${role}: ${edge.label}`,
                    raw: `${sourceSymbol} ${role} ${edge.label}`,
                    resolved: true,
                    kind: edge.kind === "extends" || edge.kind === "implements" ? edge.kind : "dependency"
                });
            }
        }

        // A dependency line with no target inside the scan: a package, a builtin, or a file the scan does not hold.
        const missingDependencies = dependencies.filter(reference => !reference.resolved);
        dependenciesOutsideScan += missingDependencies.length;

        for (const reference of dependencies) {
            // Signature references remain available in the dependency list, but
            // become links only in the type-reference pass below. Emitting both
            // made a single documented fact count twice in the 3D overview.
            const signature = reference.sourceSymbol !== undefined;
            if (reference.resolved && reference.targetId && (!signature || reference.kind === "extends" || reference.kind === "implements")) {
                addLink(file.codePath, reference.targetId, reference.kind, reference.sourceSymbol, nameOfAnchor(reference.targetId, reference.targetSymbol));
            }
        }

        return {
            id: file.codePath,
            name: file.codePath.slice(file.codePath.lastIndexOf("/") + 1),
            codePath: file.codePath,
            codeRelativePath: file.codePath,
            docPath: file.docPath,
            docRelativePath: file.docPath,
            archetype: file.archetype ?? "implementation",
            dependencies,
            dependents: file.inbound,
            missingDependencies,
            publicSymbols: file.symbols.map(symbol => symbol.name),
            publicSymbolsExtended: publicSymbolsExtendedOf(file),
            ...(file.entries.length ? { entryReasons: file.entries.map(entry => entry.reason) } : {})
        };
    });

    // Type references become edges of their own kind, after every dependency edge:
    // the providing file is the target, the consuming symbol the source.
    for (const node of nodes) {
        for (const symbol of node.publicSymbolsExtended ?? []) {
            for (const reference of symbol.typeReferences ?? []) {
                if (!reference.isResolved || !reference.targetId) {
                    continue;
                }
                if (reference.role === "extends" || reference.role === "implements") {
                    continue;
                }
                addLink(node.id, reference.targetId, "type-reference", symbol.name,
                    reference.targetAnchor ? nameOfAnchor(reference.targetId, reference.targetAnchor) : reference.typeName);
            }
        }
    }

    return {
        nodes,
        links,
        stats: {
            nodes: nodes.length,
            links: links.length,
            references: documentedReferences(links),
            dependenciesOutsideScan
        }
    };
}

/**
 * How many documented references the links carry between two files of the
 * scan: each link counts its `referenceCount`, one when it has none. A link
 * from a signature that names the same type in three places is one link and
 * three references. Today's stats and a moment of the history count through
 * this one function, and Knowledge Sources reads today's, so that no two
 * places in the Explorer give the number differently (2026-10-30).
 */
export function documentedReferences(links: readonly ExplorerLinkPayload[]): number {
    return links.reduce((total, link) => total + (link.referenceCount ?? 1), 0);
}

/**
 * Each symbol with its type references. A reference to another file is
 * resolved; a reference to a type of the same file, or to no doc, is not, which
 * is how the views tell a self-reference from a cross-file one. A bare type
 * name keeps its `[]` suffix, as the views were written to expect.
 */
function publicSymbolsExtendedOf(file: GraphFile): ExplorerPublicSymbol[] {
    const referenceEdges = file.edges.filter(isReference);
    let next = 0;
    return file.symbols.map(symbol => {
        const typeReferences: ExplorerTypeReference[] = [];
        for (const line of symbol.references) {
            const entries = line.role === "Parameters"
                ? line.parameters.flatMap(parameter => parameter.types.map(type => ({ type, role: "parameter" as const, parameterName: parameter.name })))
                : line.types.map(type => ({ type, role: ROLE_OF_LINE[line.role], parameterName: undefined }));
            for (const { type, role, parameterName } of entries) {
                if (!type.link) {
                    typeReferences.push({ typeName: `${type.name}${type.array ? "[]" : ""}`, role, parameterName, isResolved: false });
                    continue;
                }
                const edge = referenceEdges[next++];
                const targetId = edge.to !== undefined && edge.to !== file.codePath ? edge.to : undefined;
                typeReferences.push({
                    typeName: type.name,
                    role,
                    parameterName,
                    isResolved: targetId !== undefined,
                    targetId,
                    targetAnchor: edge.toSymbol
                });
            }
        }
        return { name: symbol.name, anchor: symbol.slug, ...(typeReferences.length ? { typeReferences } : {}) };
    });
}
